import React from 'react'
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { dialogBackdropProps } from '../../styles/formStyles'
import SharedButton from '../Button'

interface BaseDialogProps {
  open: boolean
  onClose: () => void
  maxWidth?: number | string
}

// ─── Form Dialog ───────────────────────────────────────────────
interface FormDialogProps extends BaseDialogProps {
  variant: 'form'
  title: string
  subtitle?: string
  children: React.ReactNode
  actions: React.ReactNode
  loading?: React.ReactNode
}

// ─── Alert Dialog (success, error, info — single button) ──────
interface AlertDialogProps extends BaseDialogProps {
  variant: 'alert'
  icon: React.ReactNode
  iconBg: string
  title: string
  description?: string
  buttonLabel?: string
}

// ─── Confirm Dialog (cancel + action buttons) ─────────────────
interface ConfirmDialogProps extends BaseDialogProps {
  variant: 'confirm'
  icon: React.ReactNode
  iconBg: string
  title: string
  description?: string
  cancelLabel?: string
  confirmLabel: string
  confirmColor?: 'error' | 'primary' | 'success'
  confirmLoading?: boolean
  onConfirm: () => void
  /** Open with focus on Cancel instead of the confirm button, so Enter cannot commit by accident. */
  focusCancel?: boolean
  /**
   * A refusal the SERVER returned for the action this dialog is confirming.
   *
   * It renders in place, under the description, and the dialog is expected to
   * stay open. A toast is the wrong surface for these: it appears behind the
   * still-open modal, and the messages that matter here are instructions
   * ("generate a new key from Settings first"), not status pings — the operator
   * has to read them at the moment they are deciding, not watch them fade.
   * Callers that pass this must silence the interceptor toast for the same call
   * or the message is reported twice.
   */
  error?: string
  /** Extra body under the description, e.g. a summary of what is being confirmed. */
  children?: React.ReactNode
}

// ─── Content Dialog (fully custom body — legacy support) ──────
interface ContentDialogProps extends BaseDialogProps {
  variant: 'content'
  width?: number
  height?: number
  children: React.ReactNode
  actions?: React.ReactNode
}

type SharedDialogProps = FormDialogProps | AlertDialogProps | ConfirmDialogProps | ContentDialogProps

const paperSx = (maxWidth?: number | string) => ({
  borderRadius: '12px',
  maxWidth: { xs: 'calc(100% - 32px)', sm: maxWidth ?? 400 },
  mx: { xs: 2, sm: 'auto' },
  width: '100%',
})

const SharedDialog = (props: SharedDialogProps) => {
  const { open, onClose, variant, maxWidth } = props

  // ── Form Dialog ──
  if (variant === 'form') {
    const { title, subtitle, children, actions, loading } = props
    return (
      <Dialog
        open={open}
        onClose={onClose}
        scroll="paper"
        aria-labelledby="dialog-title"
        maxWidth="sm"
        fullWidth
        {...dialogBackdropProps}
        PaperProps={{ sx: paperSx(maxWidth ?? 480) }}
      >
        <DialogTitle
          id="dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: '18px', fontWeight: 700, color: 'text.primary' }}>{title}</Typography>
            {subtitle && (
              <Typography sx={{ fontSize: '13px', color: 'text.secondary', mt: 0.25 }}>{subtitle}</Typography>
            )}
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close dialog"
            sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        {children}
        {actions}
        {loading}
      </Dialog>
    )
  }

  // ── Alert Dialog ──
  if (variant === 'alert') {
    const { icon, iconBg, title, description, buttonLabel = 'Done' } = props
    return (
      <Dialog open={open} onClose={onClose} {...dialogBackdropProps} PaperProps={{ sx: paperSx(maxWidth ?? 360) }}>
        <DialogContent sx={{ pt: 4, pb: 2, px: 3 }}>
          <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: iconBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              {icon}
            </Box>
            <Typography sx={{ fontSize: '18px', fontWeight: 700, color: 'text.primary', mb: 0.5 }}>{title}</Typography>
            {description && (
              <Typography sx={{ fontSize: '14px', color: '#64748b', lineHeight: 1.5 }}>{description}</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'center' }}>
          <SharedButton label={buttonLabel} fullWidth onClick={onClose} />
        </DialogActions>
      </Dialog>
    )
  }

  // ── Confirm Dialog ──
  if (variant === 'confirm') {
    const {
      icon,
      iconBg,
      title,
      description,
      cancelLabel = 'Cancel',
      confirmLabel,
      confirmColor = 'error',
      confirmLoading,
      onConfirm,
      focusCancel,
      error,
      children,
    } = props

    const btnVariantMap: Record<string, 'danger' | 'primary'> = {
      error: 'danger',
      primary: 'primary',
      success: 'primary',
    }

    return (
      <Dialog open={open} onClose={onClose} {...dialogBackdropProps} PaperProps={{ sx: paperSx(maxWidth ?? 400) }}>
        <DialogContent sx={{ pt: 4, pb: 2, px: 3 }}>
          <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: iconBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              {icon}
            </Box>
            <Typography sx={{ fontSize: '18px', fontWeight: 700, color: 'text.primary', mb: 1 }}>{title}</Typography>
            {description && (
              <Typography sx={{ fontSize: '14px', color: '#64748b', lineHeight: 1.5 }}>{description}</Typography>
            )}
            {children}
            {error && (
              <Box
                role="alert"
                sx={{
                  mt: 2,
                  width: '100%',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: '8px',
                  bgcolor: '#fef2f2',
                  border: '1px solid #fecaca',
                }}
              >
                <ErrorOutlineIcon sx={{ fontSize: 18, color: '#ef4444', flexShrink: 0, mt: '1px' }} />
                <Typography sx={{ fontSize: '13px', color: '#b91c1c', lineHeight: 1.5 }}>{error}</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <SharedButton
            label={cancelLabel}
            btnVariant="secondary"
            fullWidth
            onClick={onClose}
            autoFocus={focusCancel}
          />
          <SharedButton
            label={confirmLabel}
            btnVariant={btnVariantMap[confirmColor] || 'danger'}
            fullWidth
            onClick={onConfirm}
            loading={confirmLoading}
            autoFocus={!focusCancel}
          />
        </DialogActions>
      </Dialog>
    )
  }

  // ── Content Dialog (legacy) ──
  if (variant === 'content') {
    const { width, height, children, actions } = props
    return (
      <Dialog open={open} PaperProps={{ style: { width, height } }}>
        <Box display="flex" justifyContent="flex-end" p={1} sx={{ overflow: 'hidden' }}>
          {onClose && (
            <IconButton edge="end" onClick={onClose} aria-label="close" sx={{ color: 'primary.main' }}>
              <CloseIcon sx={{ fontSize: 24 }} />
            </IconButton>
          )}
        </Box>
        {children && <DialogContent sx={{ overflow: 'hidden' }}>{children}</DialogContent>}
        {actions && <DialogActions>{actions}</DialogActions>}
      </Dialog>
    )
  }

  return null
}

export default SharedDialog
